// SPDX-License-Identifier: AGPL-3.0-or-later

import {SoundType} from '@app/features/notification/utils/SoundUtils';
import {Logger} from '@app/features/platform/utils/AppLogger';
import * as SoundCommands from '@app/features/ui/commands/SoundCommands';
import MediaEngine from '@app/features/voice/engine/MediaEngineFacade';
import {stopWatchingStreamKey} from '@app/features/voice/engine/VoiceStreamWatchState';
import {usePendingVoiceConnection} from '@app/features/voice/hooks/usePendingVoiceConnection';
import {useVoiceJoinEligibility} from '@app/features/voice/hooks/useVoiceJoinEligibility';
import LocalVoiceState from '@app/features/voice/state/LocalVoiceState';
import {useCallback, useMemo} from 'react';

interface StreamWatchState {
	isWatching: boolean;
	isPendingJoin: boolean;
	canWatch: boolean;
	startWatching: () => void;
	addStream: () => void;
	stopWatching: () => void;
}
const logger = new Logger('useStreamWatchState');
export function useStreamWatchState({
	streamKey,
	guildId,
	channelId,
}: {
	streamKey: string;
	guildId: string | null | undefined;
	channelId: string | null | undefined;
}): StreamWatchState {
	const {canJoin} = useVoiceJoinEligibility({
		guildId: guildId ?? null,
		channelId: channelId ?? null,
	});
	const isConnectedToChannel = useMemo(() => {
		if (!channelId) return false;
		return MediaEngine.channelId === channelId && MediaEngine.guildId === (guildId ?? null);
	}, [channelId, guildId, MediaEngine.channelId, MediaEngine.guildId]);
	const replaceViewerStreamKeys = useCallback(
		(keys: Array<string>) => {
			const previousKeys = LocalVoiceState.getViewerStreamKeys();
			const hadStreams = previousKeys.length > 0;
			const hasStreams = keys.length > 0;
			logger.debug('Replacing viewer stream keys', {
				streamKey,
				isConnectedToChannel,
				previousKeys,
				keys,
			});
			LocalVoiceState.updateViewerStreamKeys(keys);
			MediaEngine.syncLocalVoiceStateWithServer({viewer_stream_keys: keys});
			if (hadStreams && !hasStreams) {
				SoundCommands.playSound(SoundType.ViewerLeave);
				return;
			}
			if (!hadStreams && hasStreams) {
				SoundCommands.playSound(SoundType.ViewerJoin);
				return;
			}
			if (hadStreams && hasStreams) {
				SoundCommands.playSound(SoundType.ViewerJoin);
			}
		},
		[isConnectedToChannel, streamKey],
	);
	const handleStreamConnected = useCallback(() => {
		replaceViewerStreamKeys([streamKey]);
	}, [streamKey, replaceViewerStreamKeys]);
	const {
		isPending: isPendingJoin,
		startConnection,
		cancel: cancelPendingJoin,
	} = usePendingVoiceConnection({
		guildId,
		channelId,
		onConnected: handleStreamConnected,
	});
	const isWatching = isConnectedToChannel && LocalVoiceState.getViewerStreamKeys().includes(streamKey);
	const canWatch = isConnectedToChannel || canJoin;
	const startWatching = useCallback(() => {
		if (!streamKey) return;
		if (isConnectedToChannel) {
			logger.debug('Starting stream watch in active call', {
				streamKey,
				channelId,
				guildId,
				previousKeys: LocalVoiceState.getViewerStreamKeys(),
			});
			replaceViewerStreamKeys([streamKey]);
			return;
		}
		if (!channelId || !canJoin) return;
		logger.debug('Starting stream watch with pending voice join', {streamKey, channelId, guildId});
		startConnection();
	}, [isConnectedToChannel, streamKey, channelId, canJoin, startConnection, replaceViewerStreamKeys, guildId]);
	const addStream = useCallback(() => {
		if (!streamKey) return;
		if (!isConnectedToChannel) {
			logger.debug('Add stream requested while disconnected, falling back to startWatching', {
				streamKey,
				channelId,
				guildId,
			});
			startWatching();
			return;
		}
		if (LocalVoiceState.getViewerStreamKeys().includes(streamKey)) return;
		const updated = [...LocalVoiceState.getViewerStreamKeys(), streamKey];
		logger.debug('Adding stream to watcher set', {
			streamKey,
			channelId,
			guildId,
			previousKeys: LocalVoiceState.getViewerStreamKeys(),
			updatedKeys: updated,
		});
		LocalVoiceState.updateViewerStreamKeys(updated);
		MediaEngine.syncLocalVoiceStateWithServer({viewer_stream_keys: updated});
		SoundCommands.playSound(SoundType.ViewerJoin);
	}, [streamKey, isConnectedToChannel, startWatching, channelId, guildId]);
	const stopWatching = useCallback(() => {
		cancelPendingJoin();
		if (!streamKey) return;
		logger.debug('Stopping stream watch', {
			streamKey,
			channelId,
			guildId,
			previousKeys: LocalVoiceState.getViewerStreamKeys(),
		});
		if (stopWatchingStreamKey(streamKey, {guildId, channelId})) {
			SoundCommands.playSound(SoundType.ViewerLeave);
		}
	}, [streamKey, cancelPendingJoin, channelId, guildId]);
	return {isWatching, isPendingJoin, canWatch, startWatching, addStream, stopWatching};
}
